import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';

import {
  registGroup as mutationRegistGroup,
  registMailToken as mutationRegistMailToken,
  registReservation as mutationRegistReservation
} from '@graphql/mutations';
import {
  getAreaInfo,
  getAreaList,
  getAreaReservationList,
  getAreaUsageStatus,
  getEmailFromToken,
  getEquipmentInfo,
  getEquipmentList,
  getFacilityInfo,
  getGroupDetail,
  getReservationMasterData,
  getSectionInfoList,
  getSectionUsageStatus,
  getSectionUsageStatusWithCondition
} from '@graphql/queries';

import {
  GetAreaInfoQueryVariables,
  GetAreaListQueryVariables,
  GetAreaUsageStatusQueryVariables,
  GetEmailFromTokenQueryVariables,
  GetEquipmentInfoQueryVariables,
  GetEquipmentListQueryVariables,
  GetFacilityInfoQueryVariables,
  GetGroupDetailQueryVariables,
  GetReservationMasterDataQueryVariables,
  GetSectionInfoListQueryVariables,
  GetSectionUsageStatusQueryVariables,
  RegistGroupMutationVariables,
  RegistMailTokenMutationVariables,
  RegistReservationMutationVariables,
  GetAreaReservationListQueryVariables,
  getAreaInfoType,
  getAreaListType,
  getAreaReservationListType,
  getEmailFromTokenType,
  getEquipmentInfoType,
  getEquipmentListType,
  getFacilityInfoType,
  getGroupDetailType,
  getReservationMasterDataType,
  getSectionInfoListType,
  registGroupType,
  registMailTokenType,
  registReservationType,
  GetSectionUsageStatusWithConditionQueryVariables,
  getSectionUsageStatusWithConditionType
} from '../API';

const client = generateClient();

/**
 *
 * ログインされてたら
 *
 */

const getAuthMode = async (): Promise<'userPool' | 'iam'> => {
  try {
    const { username } = await getCurrentUser();
    if (username) {
      return 'userPool';
    }
  } catch (err) {
    // console.log(err);
    // エラーは不要なため、非表示とする
  }
  // configから取得する
  return 'iam';
};

/**
 * 団体情報登録
 *
 */
export const registGroupApi = async (params: RegistGroupMutationVariables): Promise<registGroupType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: mutationRegistGroup,
    variables: params
  });

  if ('data' in result && result.data) {
    const { data } = result;
    const registGroup = data.registGroup as unknown as registGroupType;

    return registGroup;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * メールトークン登録登録
 *
 */
export const registMailTokenApi = async (params: RegistMailTokenMutationVariables): Promise<registMailTokenType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: mutationRegistMailToken,
    variables: params
  });

  if ('data' in result && result.data) {
    const { data } = result;
    const mailToken = data.registMailToken as unknown as registMailTokenType;

    return mailToken;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * トークンよりメールアドレス取得
 *
 */
export const getEmailFromTokenApi = async (params: GetEmailFromTokenQueryVariables): Promise<getEmailFromTokenType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getEmailFromToken,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetEmailFromToken = data.getEmailFromToken as unknown as getEmailFromTokenType;

    return resGetEmailFromToken;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * 区画予約情報取得
 *
 */
export const getSectionUsageStatusApi = async (params: GetSectionUsageStatusQueryVariables): Promise<string> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getSectionUsageStatus,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetSectionUsageStatus = data.getSectionUsageStatus as unknown as string;

    return resGetSectionUsageStatus;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * 区画予約情報取得(区画・時間で指定)
 *
 */
export const getSectionUsageStatusWithConditionApi = async (params: GetSectionUsageStatusWithConditionQueryVariables): Promise<getSectionUsageStatusWithConditionType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getSectionUsageStatusWithCondition,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetSectionUsageStatusWithCondition = data.getSectionUsageStatusWithCondition as getSectionUsageStatusWithConditionType;

    return resGetSectionUsageStatusWithCondition;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * 週間予約情報取得
 *
 */
export const getAreaUsageStatusApi = async (params: GetAreaUsageStatusQueryVariables): Promise<string> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getAreaUsageStatus,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetAreaUsageStatus = data.getAreaUsageStatus as unknown as string;

    return resGetAreaUsageStatus;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * エリア一覧取得
 *
 */
export const getEquipmentListApi = async (params: GetEquipmentListQueryVariables): Promise<getEquipmentListType[]> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getEquipmentList,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetEquipmentList = data.getEquipmentList as unknown as getEquipmentListType[];

    return resGetEquipmentList;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * エリア情報取得
 *
 */
export const getAreaInfoApi = async (params: GetAreaInfoQueryVariables): Promise<getAreaInfoType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getAreaInfo,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetAreaInfo = data.getAreaInfo as unknown as getAreaInfoType;

    return resGetAreaInfo;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * 備品一覧取得
 *
 */
export const getAreaListApi = async (params: GetAreaListQueryVariables): Promise<getAreaListType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getAreaList,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetAreaList = data.getAreaList as unknown as getAreaListType;

    return resGetAreaList;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * 備品情報取得
 *
 */
export const getEquipmentInfoApi = async (params: GetEquipmentInfoQueryVariables): Promise<getEquipmentInfoType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getEquipmentInfo,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetEquipmentInfo = data.getEquipmentInfo as unknown as getEquipmentInfoType;

    return resGetEquipmentInfo;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * マスタ情報取得（利用目的）
 *
 */
export const getReservationMasterDataApi = async (
  params: GetReservationMasterDataQueryVariables
): Promise<getReservationMasterDataType[]> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getReservationMasterData,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetReservationMasterData = data.getReservationMasterData as unknown as getReservationMasterDataType[];

    return resGetReservationMasterData;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * 仮予約情報登録
 *
 */
export const registReservationApi = async (
  params: RegistReservationMutationVariables
): Promise<registReservationType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: mutationRegistReservation,
    variables: params
  });

  if ('data' in result && result.data) {
    const { data } = result;
    const registReservation = data.registReservation as unknown as registReservationType;

    return registReservation;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};

/**
 * エリア一覧取得
 *
 */
export const getSectionInfoListApi = async (
  params: GetSectionInfoListQueryVariables
): Promise<getSectionInfoListType[]> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getSectionInfoList,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetSectionInfoList = data.getSectionInfoList as unknown as getSectionInfoListType[];

    return resGetSectionInfoList;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};
/**
 * 団体情報取得
 *
 */
export const getGroupDetailApi = async (params: GetGroupDetailQueryVariables): Promise<getGroupDetailType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getGroupDetail,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetGroupDetail = data.getGroupDetail as unknown as getGroupDetailType;

    return resGetGroupDetail;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};
/**
 * 施設名称取得
 *
 */
export const getFacilityInfoApi = async (params: GetFacilityInfoQueryVariables): Promise<getFacilityInfoType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getFacilityInfo,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetFacilityInfo = data.getFacilityInfo as unknown as getFacilityInfoType;

    return resGetFacilityInfo;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};
/**
 * 施設名称取得
 *
 */
export const getAreaReservationListApi = async (params: GetAreaReservationListQueryVariables): Promise<getAreaReservationListType> => {
  const result = await client.graphql({
    authMode: await getAuthMode(),
    query: getAreaReservationList,
    variables: params
  });
  if ('data' in result && result.data) {
    const { data } = result;
    const resGetAreaReservationList = data.getAreaReservationList as unknown as getAreaReservationListType;

    return resGetAreaReservationList;
  }
  return Promise.reject(new Error('GraphQL APIの結果に data が含まれていません。'));
};
export const temp = '';
